import React, {useRef, useEffect, useState, useMemo, useCallback} from "react"
import {useDispatch, useSelector} from "react-redux"
import styled from "styled-components"
import moment from "moment-timezone"
import {useAuth0} from "@auth0/auth0-react"
// UI
import {Row, Col} from "@uprise/grid"
import {Modal} from "@uprise/modal"
import {H5, H4} from "@uprise/headings"
import {Button} from "@uprise/button"
import {spacing} from "@uprise/spacing"
import {Card} from "@uprise/card"
import {Alert} from "@uprise/alert"
import {primary} from "@uprise/colors"
import {Medium} from "@uprise/text"
// Slices
import {
	userDataSelector,
    fetchBookingsByUserId,
	fetchSessionDetailsByUser,
	fetchUsersByEmail,
	fetchCodeCaps
} from "features/Users/usersSlice"
import {authSelector} from "features/Auth/authSlice"
import {fetchEmployerByCode, employerSelector} from "features/Employers/employerSlice"
import {bookingDataSelector, cancelBooking, fetchCoaches, clearBookingSlot} from "features/Bookings/bookingSlice"
import {coachDataSelector, fetchCoachById} from "features/Coaches/coachesSlice"
import {fetchRiskCallbackNoteByUser, riskCallbackDataSelector} from "features/RiskCallBacks/riskCallbackSlice"
// Styles
import {ButtonSmall, ButtonWarning} from "../index.style"
import {ModalButtonContainer, RadioLabel, RadioDiv} from "../../../Bookings/AllBookingsTable/index.style"
// Helpers
import {cancelReason, cancelStatus} from "helpers/constants"
// Components
import NewBooking from "./NewBooking"
import ExtraSessions from "./ExtraSessions"
import TableComponent from "components/shared/Table"
import CompleteSession from "../CompleteSession"

export const HeaderTableContent = styled.div`
	display: flex;
`

export const CustomButton = styled(Button)`
	margin: 5px;
`

const Input = styled.input`
	margin-bottom: ${spacing.s3};
`

const LeftAlign = styled.div`
	text-align: left;
`

const Bookings = () => {
	const dispatch = useDispatch()
	const {getAccessTokenSilently} = useAuth0()
	const [openModal, setOpenModal] = useState(false)
	const [bookingModal, setBookingModal] = useState(false)
	const [extraSessionModal, setExtraSessionModal] = useState(false)
	const [selectedUserBookingToDelete, setSelectedUserBookingToDelete] = useState(null)
	const [cancellingBookingBefore24Hours, setCancellingBookingBefore24Hours] = useState(false)
	const [cancelReasonState, setCancelReasonState] = useState("within24")
	const [futureBookings, setFutureBookings] = useState([])
	const [pastBookings, setPastBookings] = useState([])
	const [cancelledBookings, setCancelledBookings] = useState([])
	const [f2fBookings, setF2fBookings] = useState([])
	const [callsRemaining, setCallsRemaining] = useState(0)
	const [capsReached, setCapsReached] = useState(false)
	const [capsReachedTherapy, setCapsReachedTherapy] = useState(false)
	const [status, setStatus] = useState("coach_cancelled")
	const [therapyCallsRemaining, setTherapyCallsRemaining] = useState(0)
	const [isCombined, setIsCombined] = useState(false)
	const [tableData, setTableData] = useState([])
	const [pageCount, setPageCount] = useState(0)
	const [reschedule, setReschedule] = useState(false)
	const [sessionModal, setSessionModal] = useState(false)
	const [selectedBooking, setSelectedBooking] = useState({})
	const [currentNote, setCurrentNote] = useState("")
	const [codeExpired, setCodeExpired] = useState(false)
	const [assignedCoach, setAssignedCoach] = useState()
	const [showSessionsBtn, setShowSessionsBtn] = useState(false)
	const [sessionsEnabled, setSessionsEnabled] = useState(false)
	const [capsText, setCapsText] = useState("")

	const {bookings, profile, fetchSessionDetailsApiState, updatedSessionApiState, fetchCodeCapsApiState} =
		useSelector(userDataSelector)
	const {cancelBookingApiState, fetchCoachesApiState, createBookingApiState} = useSelector(bookingDataSelector)
	const {selectedEmployerDetails} = useSelector(employerSelector)
	const {fetchCoachByIdApiState} = useSelector(coachDataSelector)
	const authData = useSelector(authSelector)
	const {riskCallBackNoteByUserApiState} = useSelector(riskCallbackDataSelector)

	const fetchIdRef = useRef(0)
	const token = useRef(null)

	const _handleCancelBooking = async (booking = selectedUserBookingToDelete, resched = false) => {
		const authToken = await getAccessTokenSilently()

		dispatch(
			cancelBooking({
				coachId: booking.coachId,
				eventId: booking.eventId,
				bookingId: booking.id,
				userEmail: booking.email,
				cancelReason: resched ? "inTime" : cancelReasonState,
				token: authToken,
				status: status === "coach_cancelled"
			})
		).then(() => {
			if (resched) {
				setReschedule(true)
				setBookingModal(true)
			}
		})
		setOpenModal(false)
	}

	const _checkCancelOption = value => {
		setCancelReasonState(value)
	}

	const _checkCancelStatus = value => {
		setStatus(value)
	}

	const _handleModal = booking => {
		setSelectedUserBookingToDelete(booking)
		let bookingStart = moment(booking.start).utc().subtract(1, "days")
		let currentTime = moment().utc()

		if (bookingStart > currentTime) {
			setCancellingBookingBefore24Hours(true)
		}
	}

	const getToken = () => {
		if (!token.current) {
			return new Promise(async (res, rej) => {
				try {
					const authToken = await getAccessTokenSilently()
					token.current = authToken
					res(authToken)
				} catch (err) {
					console.log(err)
					rej(err)
				}
			})
		} else return Promise.resolve()
	}

	useEffect(() => {
		getToken().then(res => {
			dispatch(fetchBookingsByUserId({userId: profile.userId, token: token.current}))
			//dispatch(fetchEmployerByCode({code: profile.employerCode, token: token.current}))
			dispatch(fetchCoaches({token: token.current}))
			dispatch(fetchCodeCaps({token: token.current, code: profile.employerCode}))
			dispatch(
				fetchRiskCallbackNoteByUser({
					email: profile.email,
					token: token.current
				})
			)
			dispatch(
				fetchSessionDetailsByUser({
					email: profile.email,
					token: token.current
				})
			)
		})
	}, [])

	useEffect(() => {
		if (cancelBookingApiState.isSuccess || createBookingApiState.isSuccess || updatedSessionApiState.isSuccess) {
			getToken().then(res => {
                dispatch(fetchBookingsByUserId({userId: profile.userId, token: token.current}))
				dispatch(fetchUsersByEmail({email: profile.email, token: token.current}))
				dispatch(fetchEmployerByCode({code: profile.employerCode, token: token.current}))
				dispatch(
					fetchSessionDetailsByUser({
						email: profile.email,
						token: token.current
					})
				)
			})
		}
	}, [cancelBookingApiState, createBookingApiState, updatedSessionApiState])

	useEffect(() => {
		const id = authData?.data?.id
		if (id) {
			getToken().then(res => {
				dispatch(fetchCoachById({id, token: token.current}))
			})
		}
	}, [authData?.data?.id])

	useEffect(() => {
		getAssignedCoach()
	}, [profile.coach, bookings])

	const getAssignedCoach = () => {
		const {coach} = profile
		if (coach) setAssignedCoach(coach)
		else if (!bookings.length) return
		else {
			const lastBooking = [...bookings].reverse().find(book => !book.cancelled)
			setAssignedCoach(lastBooking?.coachEmail)
		}
	}

	const fetchData = useCallback(
		({pageSize, pageIndex}) => {
			// This will get called when the table needs new data
			// You could fetch your data from literally anywhere,
			// even a server. But for this example, we'll just fake it.

			// Give this fetch an ID
			let bookingsData = JSON.parse(JSON.stringify(bookings)) || []
			bookingsData = bookingsData.sort((a, b) => b?.start?.localeCompare(a?.start))

			const fetchId = ++fetchIdRef.current
			if (fetchId === fetchIdRef.current) {
				setTableData([...bookingsData].slice(pageSize * pageIndex, (pageIndex + 1) * pageSize))
			}
		},
		[bookings]
	)

	const {extraSessions, sessionsLeft, totalExtraSessions} = profile

	useEffect(() => {
		if (bookings?.length) setPageCount(Math.ceil(bookings.length / 20))
		const {coachingCallCap, therapyCallCap, startingDate, combined} = fetchCodeCapsApiState
		let future = [],
			past = [],
			cancelled = [],
			f2f = []
		let therapyBookings = []
		let totalValidBookings =
			bookings
				?.filter(booking => {
					return booking?.id
				})
				.filter(booking => moment(booking.createdAt).diff(moment(startingDate), "minutes") >= 0)
				.filter(booking => {
					if (["within24", "within_24", "userNoShow", "user_no_show"].includes(booking.cancelReason))
						return true
					else return !booking.cancelled
				}) || []

		bookings?.map(booking => {
			if (booking.cancelled) {
				cancelled.push(booking)
			} else if (booking.type === "F2F") f2f.push(booking)
			else if (moment(booking.start).unix() >= moment().unix()) future.push(booking)
			else past.push(booking)
		})

		const totalBookings = [...totalValidBookings]

		//therapy and coaching calls counted separately
		therapyBookings = totalValidBookings.filter(booking => booking.duration === 60)
		totalValidBookings = totalValidBookings.filter(booking => booking.duration === 30)

		const sessEnabled = selectedEmployerDetails?.data?.plan?.featurePricing?.find(
			feature => feature?.id === "additional-sessions"
		)?.enabled
		setSessionsEnabled(sessEnabled)

		let showSession = false

		if (!selectedEmployerDetails?.caps?.capCalls) {
			setCallsRemaining("unlimited")
			setTherapyCallsRemaining("unlimited")
			setCapsReached(false)
			setCapsReachedTherapy(false)
		} else {
			if (combined) {
				if (totalBookings.length >= parseInt(coachingCallCap)) {
					setCapsReached(true)
					setCapsReachedTherapy(true)
				} else {
					setCapsReached(false)
					setCapsReachedTherapy(false)
				}

				const remaining = parseInt(coachingCallCap) - totalBookings.length

				setCallsRemaining(remaining)
				setTherapyCallsRemaining(remaining)

				showSession = remaining <= 0
			} else {
				if (totalValidBookings.length >= parseInt(coachingCallCap)) {
					setCapsReached(true)
				} else setCapsReached(false)

				if (therapyBookings.length >= parseInt(therapyCallCap)) {
					setCapsReachedTherapy(true)
				} else setCapsReachedTherapy(false)

				const remaining = parseInt(coachingCallCap) - totalValidBookings.length
				const remainingTherapy = parseInt(therapyCallCap) - therapyBookings.length

				setCallsRemaining(remaining)
				setTherapyCallsRemaining(remainingTherapy)

				showSession = remaining + remainingTherapy <= 0
			}
		}

		setShowSessionsBtn(showSession)

		if (extraSessions && sessionsLeft) {
			setShowSessionsBtn(false)
		}

		setCodeExpired(moment().isAfter(moment(selectedEmployerDetails.expiryDate)))
		setFutureBookings(future)
		setPastBookings(past)
		setF2fBookings(f2f)
		setCancelledBookings(cancelled)
		setIsCombined(combined)
	}, [bookings, selectedEmployerDetails, extraSessions, sessionsLeft, fetchCodeCapsApiState])

	useEffect(() => {
		if (isCombined) setCapsText(`${callsRemaining} calls remaining`)
		else setCapsText(`${callsRemaining} coaching and ${therapyCallsRemaining} therapy calls remaining`)
	}, [callsRemaining, therapyCallsRemaining, isCombined])

	const columns = useMemo(
		() => [
			{
				Header: "Date",
				accessor: "start",
				desc: true,
				sortType: "string",
				Cell: cellInfo => <div>{moment(cellInfo.row.original.start).format("DD-MMM-YYYY")}</div>,
				width: 100,
				show: true
			},
			{
				Header: "Time",
				accessor: "end",
				Cell: cellInfo => {
					const {start} = cellInfo.row.original
					const timezone = cellInfo.row.original.timezone || profile.timezone
					return <LeftAlign>{moment(start).tz(timezone).format("hh:mm A z")}</LeftAlign>
				},
				show: true
			},
			{
				Header: "Duration",
				accessor: "duration",
				Cell: cellInfo => <LeftAlign>{cellInfo.row.original.duration} mins</LeftAlign>,
				show: true
			},
			{
				Header: "Coach",
				accessor: "coachName",
				Cell: cellInfo => <div style={{color: primary.purple}}>{cellInfo.row.original.coachName}</div>,
				show: true
			},
			{
				Header: "Issue",
				accessor: "issues",
				width: 150,
				show: true
			},
			{
				Header: "Action",
				accessor: "cancelled",
				disableSortBy: true,
				width: 250,
				Cell: cellInfo => {
					const {cancelled, start} = cellInfo.row.original
					return (
						<Row>
							{cancelled ? (
								<Col className={"text-center"}> Cancelled </Col>
							) : (
								<Col className={"d-flex justify-content-between"}>
									<ButtonSmall
										className='m-t-0'
										variant='tertiary'
										title='reschedule'
										size='small'
										loading={true}
										fullWidth={false}
										onClick={() => {
											setSelectedUserBookingToDelete(cellInfo.row.original)
											setReschedule(true)
											setBookingModal(true)
										}}
									/>
									<ButtonWarning
										className='m-t-0'
										variant='tertiary'
										title='cancel'
										size='small'
										fullWidth={false}
										onClick={() => {
											setOpenModal(true)
											_handleModal(cellInfo.row.original)
										}}
									/>
									<ButtonWarning
										className='m-t-0'
										variant='tertiary'
										title='manage'
										size='small'
										fullWidth={false}
										onClick={() => {
											setSessionModal(true)
											setSelectedBooking(cellInfo.row.original)
											const curr = fetchSessionDetailsApiState.data.filter(
												note => note.bookingId === cellInfo.row.original.id
											)
											setCurrentNote(curr?.[0]?.note || "")
										}}
									/>
								</Col>
							)}
						</Row>
					)
				},
				show: true
			}
		],
		[bookings, fetchSessionDetailsApiState]
	)

	const onCancel = () => {
		setSessionModal(false)
		setBookingModal(false)
		setExtraSessionModal(false)
		dispatch(clearBookingSlot())
		setReschedule(false)
	}

	return (
		<Card padding={spacing.s10}>
			<Row className='justify-content-between'>
				<Col className='col-12 col-lg-12 col-md-12 col-sm-12'>
					{sessionsLeft ? (
						<Alert className='m-b-10' textAlign='center' type={"primary"}>
							{`The user’s company approved ${totalExtraSessions} extra calls for them. The user has ${sessionsLeft} calls available`}
						</Alert>
					) : (
						<Alert
							className='m-b-10'
							textAlign='center'
							type={capsReached && capsReachedTherapy ? "error" : "primary"}>
							{`${profile?.name} has ${
								capsReached && capsReachedTherapy ? `reached the maximum bookings` : capsText
							}`}
						</Alert>
					)}
				</Col>
				{!sessionsEnabled && showSessionsBtn && (
					<Col className='col-12 col-lg-12 col-md-12 col-sm-12 mb-4 justify-center d-flex'>
						<Medium>Additional sessions not available for this employer code.</Medium>
					</Col>
				)}
				<Col className='col-12 col-lg-12 col-md-12 col-sm-12 mb-4 justify-center d-flex'>
					<Button
						variant='primary'
						size='small'
						title='+  Create New Booking'
						fullWidth={false}
						onClick={() => setBookingModal(true)}
						style={{fontSize: 13, paddingLeft: 20, paddingRight: 20}}
					/>
					{showSessionsBtn && sessionsEnabled && (
						<Button
							variant='primary'
							size='small'
							title='+  Request Extra Sessions'
							fullWidth={false}
							onClick={() => setExtraSessionModal(true)}
							style={{fontSize: 13, paddingLeft: 20, paddingRight: 20, marginLeft: 20}}
						/>
					)}
				</Col>

				<Col className='col-12 col-lg-12 col-md-12 col-sm-12'>
					<TableComponent
						pageCount={pageCount}
						fetchData={fetchData}
						loading={false}
						columns={columns}
						data={tableData}
						//autoResetPage={resetPages}
						alignHeader={"start"}
					/>
				</Col>
			</Row>
			<Modal
				isOpen={bookingModal}
				handleClose={onCancel}
				padding={"24px"}
				backgroundColor={"#ffffff"}
				width={"70%"}
				scroll={true}
				textAlign='center'>
				<H4>{`Book a Session`}</H4>
				<ModalButtonContainer>
					<NewBooking
						onCancel={onCancel}
						user={profile}
						coach={fetchCoachByIdApiState.coach}
						coaches={fetchCoachesApiState.data}
						authData={authData.data}
						reschedule={reschedule}
						booking={selectedUserBookingToDelete}
					/>
				</ModalButtonContainer>
			</Modal>
			<Modal
				isOpen={extraSessionModal}
				handleClose={onCancel}
				padding={"24px"}
				backgroundColor={"#ffffff"}
				width={"70%"}
				scroll={true}
				textAlign='center'>
				<H4>{`Request Extra Sessions`}</H4>
				<ModalButtonContainer>
					<ExtraSessions onCancel={onCancel} userEmail={profile.email} />
				</ModalButtonContainer>
			</Modal>
			<Modal
				isOpen={sessionModal}
				handleClose={onCancel}
				padding={"24px 0"}
				backgroundColor={"#ffffff"}
				width={"70%"}
				scroll={true}
				textAlign='center'>
				<H4>{`Complete Session Details`}</H4>
				<ModalButtonContainer>
					<CompleteSession
						onCancel={onCancel}
						rcbNotes={riskCallBackNoteByUserApiState.data}
						coachingRemaining={callsRemaining + " remaining"}
						therapyRemaining={therapyCallsRemaining + " remaining"}
						booking={selectedBooking}
						sessionNote={currentNote}
						codeExpired={codeExpired}
						assignedCoach={assignedCoach}
					/>
				</ModalButtonContainer>
			</Modal>
			<Modal
				isOpen={openModal}
				handleClose={() => setOpenModal(false)}
				padding={"24px"}
				backgroundColor={"#ffffff"}
				width={"20%"}
				textAlign='center'>
				<H4>{`Cancel Booking`}</H4>
				<ModalButtonContainer>
					<div>
						<H5 className='m-t-0 m-b-10'>Booking was cancelled:</H5>
						<H5 textAlign={"left"} className='m-b-0'>
							Status:
						</H5>
						<RadioDiv className='radio'>
							<RadioLabel>
								<input
									className='radio-input'
									type='radio'
									name='status'
									checked={status === "coach_cancelled"}
									onClick={() => _checkCancelStatus("coach_cancelled")}
								/>
								<span>{cancelStatus["coach_cancelled"]}</span>
							</RadioLabel>
						</RadioDiv>
						<RadioDiv className='radio'>
							<RadioLabel>
								<input
									className='radio-input'
									type='radio'
									name='status'
									checked={status === "user_cancelled"}
									onClick={() => _checkCancelStatus("user_cancelled")}
								/>
								{cancelStatus["user_cancelled"]}
							</RadioLabel>
						</RadioDiv>
						<H5 textAlign={"left"} className='m-b-0'>
							Cancel Reason:
						</H5>
						<RadioDiv className='radio'>
							<RadioLabel>
								<input
									className='radio-input'
									type='radio'
									name='cancelReason'
									checked={cancelReasonState === "inTime"}
									onClick={() => _checkCancelOption("inTime")}
								/>
								<span>{cancelReason["inTime"]}</span>
							</RadioLabel>
						</RadioDiv>
						<RadioDiv className='radio'>
							<RadioLabel>
								<input
									className='radio-input'
									type='radio'
									name='cancelReason'
									checked={cancelReasonState === "within24"}
									onClick={() => _checkCancelOption("within24")}
								/>
								{cancelReason["within24"]}
							</RadioLabel>
						</RadioDiv>
						<RadioDiv className='radio'>
							<RadioLabel>
								<input
									className='radio-input'
									type='radio'
									name='cancelReason'
									checked={cancelReasonState === "userNoShow"}
									onClick={() => _checkCancelOption("userNoShow")}
								/>
								{cancelReason["userNoShow"]}
							</RadioLabel>
						</RadioDiv>
						<HeaderTableContent className='m-t-10'>
							<CustomButton
								title='Cancel'
								size='medium'
								onClick={() => setOpenModal(false)}
								variant='secondary'
							/>
							<CustomButton
								title='Confirm'
								onClick={() => _handleCancelBooking()}
								size='medium'
								variant='primary'
								disabled={!cancelReasonState}
							/>
						</HeaderTableContent>
					</div>
				</ModalButtonContainer>
			</Modal>
		</Card>
	)
}

export default Bookings
